import React, { useState, useEffect } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganization, useUser } from '@clerk/clerk-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faEnvelope, faClock } from '@fortawesome/free-solid-svg-icons';
import logo from 'assets/icons/logo.png';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import CgButton from 'modules/Common/ui/CgButton';
import TrialExtensionModal from 'modules/App/components/TrialExtensionModal';
import { OrganizationType } from 'modules/Common/utils/Utility';
import { Plan } from 'modules/Common/permissions/planGating';
import { usePermissionsContext } from 'modules/Common/permissions/PermissionsProvider';
import {
  trackTrialExpiredView,
  trackBookDemoClick,
  trackRequestExtensionClick,
  trackTrialExtensionModalOpened,
} from 'helpers/mixpanel';
import { useBranding } from '../hooks/useBranding';
import usePlanSummary from '../hooks/usePlanSummary';

const PAGE_CLASS =
  'min-h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6';
const CARD_CLASS = 'max-w-5xl w-full bg-white rounded-xl shadow-xl p-8 md:p-12';

export default function TrialExpiredPage() {
  const { organization } = useOrganization();
  const { user } = useUser();
  const brand = useBranding();
  const { data: planSummary, isLoading: planLoading } = usePlanSummary();
  const { t } = useTranslation('app');
  const history = useHistory();
  const { refresh } = usePermissionsContext();
  const [isLeaving, setIsLeaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const orgType = organization?.publicMetadata?.type;
  const isDeclarant = orgType === OrganizationType.Declarant;
  const isOperator = orgType === OrganizationType.Operator;

  const calendlyLink =
    'https://calendly.com/victor-e-0/product-demo?hide_landing_page_details=1&hide_gdpr_banner=1';

  // Track page view when component mounts
  useEffect(() => {
    trackTrialExpiredView();
  }, []);

  if (brand.key !== 'carbonglance') return <Redirect to="/" />;
  // A verifier firm has no plan to expire (verifier-firm-foundation VF18).
  if (orgType === OrganizationType.Verifier) return <Redirect to="/verifier" />;
  if (planLoading) return <CgPageLoader ariaLabel="Loading your plan" />;

  const handleGoToDashboard = async () => {
    setIsLeaving(true);
    try {
      await refresh();
    } finally {
      history.push('/');
    }
  };

  if (planSummary?.status === 'active') {
    return (
      <div className={PAGE_CLASS}>
        <div className={`${CARD_CLASS} text-center`}>
          <div className="flex justify-center mb-6">
            <img
              src={logo}
              alt={brand.name}
              className="h-16"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t('app:trialExpired.activeHeading', 'Your plan is active')}
          </h1>
          <p className="text-gray-600 mb-8 text-lg">
            {t('app:trialExpired.activeBody', 'You can pick up where you left off.')}
          </p>
          <div className="flex justify-center">
            <CgButton
              variant="primary"
              label={t('app:trialExpired.activeCta', 'Go to dashboard')}
              onClick={handleGoToDashboard}
              loading={isLeaving}
              disabled={isLeaving}
            />
          </div>
        </div>
      </div>
    );
  }

  const lapsedPlanSlug = planSummary?.plan?.slug;
  const isTrial = lapsedPlanSlug === Plan.Trial || lapsedPlanSlug === Plan.OperatorFree;

  // Prepare mailto link with pre-filled details
  const getTrialExtensionEmail = () => {
    const subject = encodeURIComponent('Trial Extension Request');
    const body = encodeURIComponent(
      `Hello ${brand.name} Team,

I would like to request a trial extension to continue testing your platform.

Organization Details:
- Organization Name: ${organization?.name || 'N/A'}
- Organization ID: ${organization?.id || 'N/A'}

User Details:
- Name: ${user?.firstName || ''} ${user?.lastName || ''}
- Email: ${user?.primaryEmailAddress?.emailAddress || 'N/A'}
- User ID: ${user?.id || 'N/A'}

I am interested in continuing to explore the platform and would appreciate an extension to complete my evaluation.

Thank you for your consideration.

Best regards,
${user?.firstName || ''} ${user?.lastName || ''}`,
    );

    return `mailto:${brand.supportEmail}?subject=${subject}&body=${body}`;
  };

  const handlePrimaryCta = () => {
    // Reuse existing tracking event for primary CTA click
    trackBookDemoClick();

    if (isDeclarant) {
      // Open email modal to request payment for declarant plan
      setShowPaymentModal(true);
      return;
    }

    if (isOperator) {
      // Redirect operators into existing payment/upgrade flow
      window.location.href = '/upgrade';
      return;
    }

    // Fallback: keep legacy behavior (book demo) if org type is unknown
    window.open(calendlyLink, '_blank', 'noopener,noreferrer');
  };

  const handleTrialExtension = () => {
    if (isTrial) {
      trackRequestExtensionClick();
      trackTrialExtensionModalOpened();
    }
    setShowModal(true);
  };

  // Shared fields for the two support-email previews below (trial extension +
  // declarant upgrade request). Computed once so the "N/A" fallback and the
  // user's full name are translated/derived consistently in both places.
  const naFallback = t('app:trialExpired.emailFields.notAvailable', 'N/A');
  const orgNameValue = organization?.name || naFallback;
  const orgIdValue = organization?.id || naFallback;
  const userFullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const userEmailValue = user?.primaryEmailAddress?.emailAddress || naFallback;
  const userIdValue = user?.id || naFallback;
  const organizationInfoLabels = {
    [t('app:trialExpired.emailFields.organizationName', 'Organization Name')]: orgNameValue,
    [t('app:trialExpired.emailFields.organizationId', 'Organization ID')]: orgIdValue,
  };
  const userInfoLabels = {
    [t('app:trialExpired.emailFields.userName', 'User Name')]: userFullName,
    [t('app:trialExpired.emailFields.userEmail', 'User Email')]: userEmailValue,
    [t('app:trialExpired.emailFields.userId', 'User ID')]: userIdValue,
  };

  const emailValues = {
    brandName: brand.name,
    orgName: orgNameValue,
    orgId: orgIdValue,
    userName: userFullName,
    userEmail: userEmailValue,
    userId: userIdValue,
  };

  return (
    // No `min-h-screen`: this page renders inside AppLayout now (so the user can
    // reach the workspace switcher), and the shell's content box already supplies
    // `min-h-[90vh]`. Forcing a second full viewport under the app header would
    // just add a scrollbar. `min-h-full` fills whatever the shell gives it.
    <div className={PAGE_CLASS}>
      <div className={CARD_CLASS}>
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src={logo}
            alt={brand.name}
            className="h-16"
          />
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
            <FontAwesomeIcon
              icon={faClock}
              className="text-orange-600 text-4xl"
            />
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-4">
          {isTrial
            ? t('app:trialExpired.heading', 'Your Free Trial Has Ended')
            : t('app:trialExpired.planEndedHeading', 'Your plan has ended')}
        </h1>

        {/* Message */}
        <p className="text-center text-gray-600 mb-8 text-lg">
          {isTrial
            ? t(
                'app:trialExpired.thankYou',
                'Thank you for trying {{brandName}}! We hope you found our platform valuable for your CBAM compliance needs.',
                { brandName: brand.name },
              )
            : t(
                'app:trialExpired.planEndedThankYou',
                'Thank you for using {{brandName}}. Renew your plan to keep working on your CBAM compliance.',
                { brandName: brand.name },
              )}
        </p>

        {/* Divider */}
        <div className="border-t border-gray-200 mb-8"></div>

        {/* Next Steps Heading */}
        <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
          {t('app:trialExpired.nextSteps', 'What would you like to do next?')}
        </h2>

        {/* CTA Options - 2 Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Book a Demo CTA */}
          <div className="border border-gray-200 rounded-lg p-6 hover:border-primaryColor hover:shadow-md transition-all flex flex-col">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="text-primaryColor text-2xl"
                />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
              {isDeclarant || isOperator
                ? t('app:trialExpired.upgradeHeading', 'Upgrade Plan')
                : t('app:trialExpired.talkHeading', 'Talk to Our Team')}
            </h3>
            <p className="text-gray-600 mb-2 text-center">
              {isDeclarant &&
                t(
                  'app:trialExpired.declarantMessage',
                  'Upgrade your account to our Declarant plan for €2,500 per entity per year.',
                )}
              {isOperator &&
                t(
                  'app:trialExpired.operatorMessage',
                  'Upgrade your account to our Operator plan for £2,500 per installation per year.',
                )}
              {!isDeclarant &&
                !isOperator &&
                t(
                  'app:trialExpired.demoMessage',
                  'Schedule a personalized demo with our team to discuss your requirements and pricing.',
                )}
            </p>
            <CgButton
              variant="primary"
              label={t('app:trialExpired.upgradeHeading', 'Upgrade Plan')}
              icon={isDeclarant ? 'faEnvelope' : 'faCalendarAlt'}
              onClick={handlePrimaryCta}
              className="w-full mt-4"
            />
          </div>

          {/* Trial Extension CTA */}
          <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-400 hover:shadow-md transition-all flex flex-col">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="text-blue-600 text-2xl"
                />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
              {isTrial
                ? t('app:trialExpired.requestExtensionTitle', 'Request Trial Extension')
                : t('app:trialExpired.renewTitle', 'Renew Your Plan')}
            </h3>
            <p className="text-gray-600 mb-4 text-center flex-1">
              {isTrial
                ? t(
                    'app:trialExpired.requestExtensionBody',
                    'Need more time to evaluate? Contact our support team to request a trial extension.',
                  )
                : t('app:trialExpired.renewBody', "Contact our team and we'll help you renew.")}
            </p>
            <CgButton
              variant="secondary"
              label={
                isTrial
                  ? t('app:trialExpired.requestExtension', 'Request Extension')
                  : t('app:trialExpired.renewCta', 'Contact Us')
              }
              icon="faEnvelope"
              onClick={handleTrialExtension}
              className="w-full"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            {t('app:trialExpired.contactPrompt', 'Have questions? Contact us at')}{' '}
            <a
              href={`mailto:${brand.supportEmail}`}
              className="text-primaryColor hover:underline font-medium"
            >
              {brand.supportEmail}
            </a>
          </p>
        </div>
      </div>

      {/* Trial Extension Modal */}
      <TrialExtensionModal
        show={showModal}
        onHide={() => setShowModal(false)}
        title={isTrial ? undefined : t('app:trialExpired.renewModalTitle', 'Request Plan Renewal')}
        primaryCtaLabel={isTrial ? undefined : t('common:email.openInEmail', 'Open in Email')}
        emailPreview={{
          to: brand.supportEmail,
          subject: isTrial
            ? t(
                'app:trialExpired.emailFields.extensionSubject',
                'Trial Extension Request - {{orgName}}',
                {
                  orgName: orgNameValue,
                },
              )
            : t(
                'app:trialExpired.emailFields.renewalSubject',
                'Plan Renewal Request - {{orgName}}',
                { orgName: orgNameValue },
              ),
          body: isTrial
            ? t(
                'app:trialExpired.emailFields.extensionBody',
                `Hello {{brandName}} Team,

I would like to request a trial extension to continue testing your platform.

Organization Details:
- Organization Name: {{orgName}}
- Organization ID: {{orgId}}

User Details:
- Name: {{userName}}
- Email: {{userEmail}}
- User ID: {{userId}}

I am interested in continuing to explore the platform and would appreciate an extension to complete my evaluation.

Thank you for your consideration.

Best regards,
{{userName}}`,
                emailValues,
              )
            : t(
                'app:trialExpired.emailFields.renewalBody',
                `Hello {{brandName}} Team,

I would like to renew our plan.

Organization Details:
- Organization Name: {{orgName}}
- Organization ID: {{orgId}}

User Details:
- Name: {{userName}}
- Email: {{userEmail}}
- User ID: {{userId}}

Please let me know the next steps to renew.

Thank you for your help.

Best regards,
{{userName}}`,
                emailValues,
              ),
        }}
        organizationInfo={organizationInfoLabels}
        userInfo={userInfoLabels}
      />
      {/* Declarant payment / upgrade request modal */}
      <TrialExtensionModal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        title={t('app:trialExpired.upgradeModalTitle', 'Request Declarant Plan Upgrade')}
        primaryCtaLabel={t('common:email.openInEmail', 'Open in Email')}
        emailPreview={{
          to: brand.supportEmail,
          subject: t(
            'app:trialExpired.emailFields.upgradeSubject',
            'Upgrade Request - Declarant Plan - {{orgName}}',
            { orgName: orgNameValue },
          ),
          body: t(
            'app:trialExpired.emailFields.upgradeBody',
            `Hello {{brandName}} Team,

I would like to upgrade my account to the Declarant plan (2,500 EUR per entity per year).

Organization Details:
- Organization Name: {{orgName}}
- Organization ID: {{orgId}}

User Details:
- Name: {{userName}}
- Email: {{userEmail}}
- User ID: {{userId}}

Please send me the invoice or payment link so I can complete the upgrade.

Thank you for your help.

Best regards,
{{userName}}`,
            {
              brandName: brand.name,
              orgName: orgNameValue,
              orgId: orgIdValue,
              userName: userFullName,
              userEmail: userEmailValue,
              userId: userIdValue,
            },
          ),
        }}
        organizationInfo={organizationInfoLabels}
        userInfo={userInfoLabels}
      />
    </div>
  );
}
